import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  BookOpen, 
  GraduationCap, 
  BarChart3, 
  Users, 
  Shield, 
  AlertCircle,
  Heart,
  Target,
  LineChart,
  MessageCircle,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dda65bda-4395-4219-89e9-934b3558952e/artifacts/kq5kdno6_WhatsApp%20Image%202026-02-19%20at%2023.17.23.jpeg";

const features = [
  {
    icon: GraduationCap,
    title: 'Öğrenci Gelişim Takibi',
    description: 'Her çocuğun okuma yolculuğunu adım adım izleyin. Güçlü yönlerini keşfedin, desteğe ihtiyaç duyduğu alanları belirleyin.'
  },
  {
    icon: Target,
    title: 'Erken Tespit Sistemi',
    description: 'Okuma güçlüğü belirtilerini erkenden fark edin. Doğru zamanda doğru desteği vererek her çocuğun potansiyeline ulaşmasını sağlayın.'
  },
  {
    icon: BookOpen,
    title: 'Zengin Kaynak Havuzu',
    description: 'Yaş ve seviyeye uygun materyaller, çalışma kağıtları ve etkinliklerle öğrenmeyi keyifli hale getirin.'
  },
  {
    icon: Heart,
    title: 'Aile Katılımı',
    description: 'Veliler çocuklarının gelişimini yakından takip edebilir. Ev etkinlikleri ve önerilerle okuma alışkanlığını birlikte destekleyin.'
  },
  {
    icon: Users,
    title: 'Ekip Koordinasyonu',
    description: 'Öğretmenler, okuma koçları ve uzmanlar arasında kesintisiz iletişim. Ortak hedefler, ortak başarılar.'
  },
  {
    icon: LineChart,
    title: 'Veri Odaklı Kararlar',
    description: 'Tarama sonuçları ve ilerleme raporlarıyla hangi stratejilerin işe yaradığını görün, müdahalelerinizi optimize edin.'
  },
];

const benefits = [
  'Okuma güçlüğü riski taşıyan öğrencileri %80 daha erken tespit edin',
  'Bireyselleştirilmiş müdahale planlarıyla başarı oranını artırın',
  'Veli-okul işbirliğini güçlendirerek ev desteğini katılımı sağlayın',
  'Tüm ekibin aynı sayfada olmasını sağlayan merkezi platform',
];

export const LoginPage = () => {
  const { login, loading } = useAuth();
  const location = useLocation();
  const errorMessage = location.state?.error;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="BilgeOkur - Okuma ve Anlama Bilimi Merkezi" 
              className="h-20 sm:h-24 w-auto object-contain"
            />
          </div>
          <Button 
            onClick={login}
            disabled={loading}
            className="bg-primary hover:bg-primary/90 shadow-md"
            data-testid="header-login-btn"
          >
            <Shield className="mr-2 h-4 w-4" />
            Giriş Yap
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-secondary/20 to-transparent rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-primary/10 to-transparent rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-16 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 text-secondary-700 text-sm font-medium mb-6">
                <BookOpen className="h-4 w-4" />
                Bilimle Okur, Bilgelikle Anlar
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-900 mb-6 leading-tight">
                Çocuklarımızın <span className="text-secondary">Okuma Yolculuğunda</span> Yanlarındayız
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed">
                BilgeOkur, öğretmenler, okuma koçları ve veliler için tasarlanmış kapsamlı bir okuma takip platformudur. 
                <strong className="text-primary-900"> Her çocuğun okumayı sevmesini</strong> ve 
                <strong className="text-primary-900"> okuduğunu anlamasını</strong> sağlamak için birlikte çalışıyoruz.
              </p>

              {errorMessage && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  size="lg"
                  onClick={login}
                  disabled={loading}
                  className="bg-primary hover:bg-primary/90 text-lg px-8 py-6 h-auto shadow-lg hover:shadow-xl transition-all"
                  data-testid="hero-login-btn"
                >
                  <Shield className="mr-2 h-5 w-5" />
                  Google ile Giriş Yap
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 py-6 h-auto border-2"
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Daha Fazla Bilgi
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>

              <p className="text-sm text-slate-500">
                Sisteme erişim için kurumunuzdan davet almanız gerekmektedir.
              </p>
            </div>

            {/* Right content - Stats */}
            <div className="lg:pl-12">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
                <h3 className="font-heading text-xl font-semibold text-primary-900 mb-6">
                  Neden BilgeOkur?
                </h3>
                <div className="space-y-4">
                  {benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="mt-1 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                      </div>
                      <p className="text-slate-700">{benefit}</p>
                    </div>
                  ))}
                </div>
                
                <div className="mt-8 pt-6 border-t border-slate-100">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-3xl font-bold text-primary-900">500+</div>
                      <div className="text-sm text-slate-500">Öğrenci</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-secondary">50+</div>
                      <div className="text-sm text-slate-500">Öğretmen</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-green-600">%95</div>
                      <div className="text-sm text-slate-500">Memnuniyet</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-primary-900 mb-4">
              Okuma Başarısı İçin Gereken Her Şey
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Tarama ve değerlendirmeden müdahale planlamasına, veli iletişiminden profesyonel gelişime kadar 
              okuma eğitiminin tüm boyutlarını tek platformda yönetin.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="group border-slate-200 hover:shadow-lg hover:border-primary/30 transition-all duration-300 hover:-translate-y-1"
              >
                <CardContent className="pt-8 pb-6">
                  <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                    <feature.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-primary-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Parent Engagement Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-50 text-pink-700 text-sm font-medium mb-6">
                <Heart className="h-4 w-4" />
                Aile Katılımı
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-primary-900 mb-6">
                Veliler de Bu Yolculuğun Bir Parçası
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Araştırmalar gösteriyor ki, <strong>aile desteği alan çocuklar okumada %40 daha hızlı ilerleme kaydediyor.</strong> 
                BilgeOkur'da veliler:
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Çocuklarının okuma gelişimini haftalık raporlarla takip edebilir</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Evde yapılabilecek etkinlik ve okuma önerileri alabilir</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Öğretmen ve koçlarla doğrudan iletişim kurabilir</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Müdahale planlarından haberdar olup sürece katkı sağlayabilir</span>
                </li>
              </ul>
              <Button 
                onClick={login}
                disabled={loading}
                className="bg-secondary hover:bg-secondary/90"
              >
                <Heart className="mr-2 h-4 w-4" />
                Veli Girişi
              </Button>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 to-pink-100 rounded-3xl transform rotate-3"></div>
              <div className="relative bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-12 w-12 rounded-full bg-pink-100 flex items-center justify-center">
                    <MessageCircle className="h-6 w-6 text-pink-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-primary-900">Öğretmenden Mesaj</div>
                    <div className="text-sm text-slate-500">Bugün 14:30</div>
                  </div>
                </div>
                <p className="text-slate-700 mb-4 italic">
                  "Merhaba! Ali bu hafta akıcı okumada büyük ilerleme kaydetti. 
                  Evde 15 dakikalık sesli okuma çalışmalarını sürdürmenizi öneririm. 
                  Harika gidiyorsunuz! 📚"
                </p>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                    AÖ
                  </div>
                  <span>Ayşe Öğretmen - 2-A Sınıf Öğretmeni</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-primary to-primary-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Çocuklarımızın Geleceği İçin Birlikte Çalışalım
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            BilgeOkur ile okulunuzda okuma eğitimini bir üst seviyeye taşıyın. 
            Kurumunuz için demo talep edin veya mevcut hesabınızla giriş yapın.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              onClick={login}
              disabled={loading}
              className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6 h-auto shadow-lg"
            >
              <Shield className="mr-2 h-5 w-5" />
              Hemen Giriş Yap
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <div className="bg-white rounded-xl p-3">
                <img 
                  src={LOGO_URL} 
                  alt="BilgeOkur - Okuma ve Anlama Bilimi Merkezi" 
                  className="h-16 w-auto object-contain"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-6 text-slate-400 text-sm">
              <span>Bilimle Okur, Bilgelikle Anlar</span>
              <span className="hidden sm:block">•</span>
              <span>© 2026 BilgeOkur Merkezi</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;
